// src/pages/chat/ChatLayout.tsx
//
// Master-detail wrapper for /chats. The left pane is always the conversation
// list (Mattermost/Telegram-style); the right pane renders the active child
// route via <Outlet /> — empty state, new-chat, or a specific thread.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useAuth, useI18n } from '@react/hooks';
import type { TranslationKey } from '@react/hooks';
import type {
  Conversation,
  ConversationMember,
  Message,
  Presence,
  PresenceStatus,
  UnreadCount,
} from '@core/types/chat';
import { AnchoredMenu } from '@/components/AnchoredMenu';
import { useFullPaneSurface } from '@/features/tabs/PaneSurface';
import { wsClient, WS_CHAT_ENABLED } from '@/features/chat/wsClient';
import {
  loadConversations,
  loadUnreadCounts,
  loadDirectConversationMembers,
  resolveConversationDisplay,
  formatRelativeTime,
  getLastMessagePreview,
  filterConversations,
  sortByLastActivity,
  loadPresences,
  updateOwnPresence,
  presenceDotClass,
  presenceStatusOptions,
  type ChatFilter,
} from '@/features/chat/logic';
import { EntityAvatar } from '@/components/EntityAvatar';
import { MessageSearchPanel } from '@/features/chat/MessageSearchPanel';
import { WorkspacePeopleGrid } from '@/features/chat/WorkspacePeopleGrid';

/** The one ghost icon button used across the chat chrome: 28px, tonal hover. */
const ghostIconBtn =
  'inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors duration-150 hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none';

const TYPE_FILTERS: Array<{ value: ChatFilter; labelKey: TranslationKey }> = [
  { value: 'all', labelKey: 'chat.filters.all' },
  { value: 'direct', labelKey: 'chat.filters.direct' },
  { value: 'group', labelKey: 'chat.filters.group' },
  { value: 'channel', labelKey: 'chat.filters.channel' },
  { value: 'entity', labelKey: 'chat.filters.entity' },
];

export const ChatLayout = observer(() => {
  // Two columns of chrome of its own: the conversation list has to start at the
  // pane's edge, next to the app menu, not 24px inside a centred column.
  const paneSurface = useFullPaneSurface();
  const { t, locale } = useI18n();
  const auth = useAuth();
  const navigate = useNavigate();
  const { id: activeId } = useParams<{ id: string }>();
  const location = useLocation();
  const isNewChatRoute = location.pathname === '/chats/new' || location.pathname === '/chats/new/';

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [membersByConv, setMembersByConv] = useState<Map<string, ConversationMember[]>>(new Map());
  const [unreadMap, setUnreadMap] = useState<Map<string, UnreadCount>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<ChatFilter>('all');
  // Entity scope: opened conversation belongs to an entity (task, document…)
  // → the list narrows to that entity's discussions until cleared.
  const [parentFilter, setParentFilter] = useState<{ id: string; type_name: string } | null>(null);

  // Presence (chat.UpdatePresence / GetPresence) — own status picker plus
  // colored dots on DM peers in the sidebar. Best-effort: never blocks or
  // errors the page, matches the ephemeral, Redis-only nature of presence
  // on the backend.
  const [ownStatus, setOwnStatus] = useState<PresenceStatus>('PRESENCE_STATUS_ONLINE');
  const [showPresenceMenu, setShowPresenceMenu] = useState(false);
  const presenceTriggerRef = useRef<HTMLButtonElement>(null);
  const [presenceMap, setPresenceMap] = useState<Map<string, Presence>>(new Map());
  const [showGlobalSearch, setShowGlobalSearch] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);

    Promise.all([
      loadConversations(),
      loadUnreadCounts(),
    ])
      .then(async ([convs, unreadRes]) => {
        if (cancelled) return;
        const sorted = sortByLastActivity(convs);
        setConversations(sorted);
        if (unreadRes?.counts) {
          const map = new Map<string, UnreadCount>();
          for (const uc of unreadRes.counts) {
            if (uc.conversation?.id) map.set(uc.conversation.id, uc);
          }
          setUnreadMap(map);
        }
        const memberMap = await loadDirectConversationMembers(sorted);
        if (!cancelled) setMembersByConv(memberMap);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Own presence — set online as soon as the chat surface mounts, away
  // while the tab is hidden, back to online (or whatever the user last
  // picked manually) when it becomes visible again. UpdatePresence is
  // Redis-only on the backend (no Kafka, no DB row) — cheap to call often.
  useEffect(() => {
    updateOwnPresence('PRESENCE_STATUS_ONLINE');
    setOwnStatus('PRESENCE_STATUS_ONLINE');
    const handleVisibility = () => {
      if (document.hidden) {
        updateOwnPresence('PRESENCE_STATUS_AWAY');
      } else {
        updateOwnPresence(ownStatus === 'PRESENCE_STATUS_AWAY' ? 'PRESENCE_STATUS_ONLINE' : ownStatus);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      updateOwnPresence('PRESENCE_STATUS_OFFLINE');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSetOwnStatus = (status: PresenceStatus) => {
    setOwnStatus(status);
    updateOwnPresence(status);
    setShowPresenceMenu(false);
  };

  // Peer presence for direct conversations — batch-fetch (chat.GetPresence)
  // whenever the DM peer set changes, then refresh on an interval so the
  // sidebar dots don't go stale during a long session.
  const dmPeerIdsKey = useMemo(() => {
    const ids = new Set<string>();
    for (const conv of conversations) {
      if (conv.type !== 'CONVERSATION_TYPE_DIRECT') continue;
      const convId = conv.entity?.id;
      if (!convId) continue;
      const members = membersByConv.get(convId) ?? [];
      for (const m of members) {
        const pid = m.profile?.entity?.id;
        if (pid && pid !== auth.activeProfileId) ids.add(pid);
      }
    }
    return Array.from(ids).sort().join(',');
  }, [conversations, membersByConv, auth.activeProfileId]);

  useEffect(() => {
    if (!dmPeerIdsKey) return;
    const ids = dmPeerIdsKey.split(',');
    let cancelled = false;
    const refresh = () => {
      loadPresences(ids).then(map => { if (!cancelled) setPresenceMap(map); });
    };
    refresh();
    const interval = window.setInterval(refresh, 30000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [dmPeerIdsKey]);

  // Live updates for the conversation sidebar — same pattern as
  // ChatListPage but mounted here too because /chats/:id renders this
  // layout (not ChatListPage), so without the subscription the sidebar
  // shows stale last_message + unread count until a manual refresh.
  // Per-conversation handler bumps the unread badge unless the sender
  // is the current user, refreshes last_message preview, re-sorts the
  // list newest-first, and zeroes the badge on local messages_read.
  const conversationIdsKey = conversations.map(c => c.entity?.id).filter(Boolean).join(',');
  useEffect(() => {
    if (!WS_CHAT_ENABLED) return;
    if (conversations.length === 0) return;
    const myProfileId = auth.activeProfileId ?? null;
    const unsubs: Array<() => void> = [];
    for (const conv of conversations) {
      const convId = conv.entity?.id;
      if (!convId) continue;
      unsubs.push(
        wsClient.subscribe('conv:' + convId, {
          onMessage: (msg: Message) => {
            if (!msg) return;
            setConversations(prev => {
              const next = prev.map(c => {
                if (c.entity?.id !== convId) return c;
                return { ...c, last_message: msg, last_activity_at: msg.created_at };
              });
              return sortByLastActivity(next);
            });
            const senderId = msg.sender?.entity?.id;
            if (senderId && senderId !== myProfileId) {
              setUnreadMap(prev => {
                const existing = prev.get(convId);
                const next = new Map(prev);
                next.set(convId, {
                  conversation: existing?.conversation ?? { id: convId } as any,
                  unread_count: (existing?.unread_count ?? 0) + 1,
                  unread_mentions: existing?.unread_mentions ?? 0,
                  last_message_at: msg.created_at,
                });
                return next;
              });
            }
          },
          onMessagesRead: (payload: { profile_id?: string }) => {
            if (!payload?.profile_id || payload.profile_id !== myProfileId) return;
            setUnreadMap(prev => {
              const existing = prev.get(convId);
              if (!existing) return prev;
              const next = new Map(prev);
              next.set(convId, { ...existing, unread_count: 0, unread_mentions: 0 });
              return next;
            });
          },
          onConversationUpdated: (updated: Conversation) => {
            if (!updated?.entity?.id) return;
            setConversations(prev =>
              prev.map(c => (c.entity?.id === updated.entity!.id ? { ...c, ...updated } : c)),
            );
          },
        }),
      );
    }
    return () => {
      for (const u of unsubs) u();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationIdsKey, auth.activeProfileId]);

  const filtered = useMemo(
    () => filterConversations(conversations, search, typeFilter).filter(
      (c: any) => !parentFilter || (c?.parent?.id === parentFilter.id && c?.parent?.type_name === parentFilter.type_name),
    ),
    [conversations, search, typeFilter, parentFilter],
  );

  // Entity scope is opt-in: opening an entity-bound chat only OFFERS the
  // scope toggle; navigating to a chat outside the scope clears it.
  const active: any = conversations.find((c: any) => (c?.entity?.id ?? c?.id) === activeId);
  const activeParent: { id: string; type_name: string } | null = active?.parent?.id ? active.parent : null;
  useEffect(() => {
    if (parentFilter && activeParent?.id !== parentFilter.id) setParentFilter(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, conversations]);

  return (
    <div className={`relative flex overflow-hidden selection:bg-accent selection:text-accent-foreground ${paneSurface}`}>
      {/* ============ Left pane — conversation list ============ */}
      <aside className={`${activeId ? 'hidden md:flex' : 'flex'} w-full md:w-80 flex-shrink-0 flex-col border-r border-border bg-muted/10`}>
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div className="relative flex items-center gap-1.5">
            <button
              ref={presenceTriggerRef}
              type="button"
              onClick={() => setShowPresenceMenu(v => !v)}
              className="flex items-center gap-1.5 rounded-md px-1.5 py-1 transition-colors duration-150 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none"
              title={t('chat.presence.setStatus')}
            >
              <span className={`h-2 w-2 rounded-full ${presenceDotClass(ownStatus)}`} />
              <h1 className="text-base font-semibold text-foreground">{t('chat.title')}</h1>
            </button>
            <AnchoredMenu
              anchor={presenceTriggerRef}
              open={showPresenceMenu}
              onClose={() => setShowPresenceMenu(false)}
              ariaLabel={t('chat.presence.setStatus')}
              className="w-44 py-1"
            >
              {presenceStatusOptions.filter(o => o.value !== 'PRESENCE_STATUS_INVISIBLE').map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  role="menuitem"
                  onClick={() => handleSetOwnStatus(opt.value as PresenceStatus)}
                  className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors duration-150 hover:bg-accent motion-reduce:transition-none ${
                    ownStatus === opt.value ? 'font-semibold text-foreground' : 'text-muted-foreground'
                  }`}
                >
                  <span className={`h-2 w-2 rounded-full ${opt.dotClass}`} />
                  {t(opt.labelKey)}
                </button>
              ))}
            </AnchoredMenu>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setShowGlobalSearch(true)}
              className={ghostIconBtn}
              title={t('chat.search.title')}
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
              </svg>
            </button>
            <Link
              to="/chats/new"
              className={ghostIconBtn}
              title={t('chat.newConversation')}
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zM19.5 7.125l-2.625-2.625" />
              </svg>
            </Link>
          </div>
        </div>

        {/* Search */}
        <div className="border-b border-border px-3 py-2">
          <div className="relative">
            <svg className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
            </svg>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('chat.searchPlaceholder')}
              className="h-8 w-full rounded-md border border-input bg-background pl-8 pr-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
        </div>

        {/* Type filter — same segmentation the task workspace uses: small
            tonal tabs over the existing client-side filter, no new plumbing. */}
        <div className="flex items-center gap-0.5 border-b border-border px-3 py-1.5">
          {TYPE_FILTERS.map(f => (
            <button
              key={f.value}
              type="button"
              onClick={() => setTypeFilter(f.value)}
              className={`rounded-md px-2 py-1 text-xs transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none ${
                typeFilter === f.value
                  ? 'bg-accent font-medium text-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-foreground'
              }`}
            >
              {t(f.labelKey)}
            </button>
          ))}
        </div>

        {/* Entity scope: the open conversation belongs to an entity (task,
            document…). Opt-in toggle narrows the list to its discussions. */}
        {parentFilter ? (
          <div className="flex items-center gap-2 border-b border-border bg-muted/40 px-3 py-1.5">
            <svg className="h-3.5 w-3.5 shrink-0 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
            </svg>
            <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
              {t('chat.entityScope', { type: parentFilter.type_name })}
            </span>
            <button
              type="button"
              onClick={() => setParentFilter(null)}
              className="shrink-0 rounded p-0.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={t('chat.entityScopeClear')}
              title={t('chat.entityScopeClear')}
            >
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        ) : activeParent ? (
          <div className="border-b border-border px-3 py-1.5">
            <button
              type="button"
              onClick={() => setParentFilter(activeParent)}
              className="flex w-full items-center gap-2 rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <svg className="h-3.5 w-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 4.5h18l-7 8v5.5l-4 2v-7.5l-7-8z" />
              </svg>
              {t('chat.entityScopeOnly')}
            </button>
          </div>
        ) : null}

        {/* List */}
        <div className="scrollbar-slim flex-1 overflow-y-auto">
          {isLoading ? (
            <div>
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 border-b border-border px-3 py-2.5">
                  <div className="h-10 w-10 animate-pulse rounded-full bg-muted" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3 w-24 animate-pulse rounded bg-muted" />
                    <div className="h-3 w-40 animate-pulse rounded bg-muted" />
                  </div>
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            search ? (
              <p className="px-4 py-12 text-center text-sm text-muted-foreground">
                {t('chat.noSearchResults')}
              </p>
            ) : (
              <WorkspacePeopleGrid currentProfileId={auth.activeProfileId} />
            )
          ) : (
            <ul>
              {filtered.map(conv => (
                <ConversationListItem
                  key={conv.entity?.id}
                  conversation={conv}
                  active={conv.entity?.id === activeId}
                  locale={locale}
                  unread={unreadMap.get(conv.entity?.id ?? '')}
                  members={membersByConv.get(conv.entity?.id ?? '')}
                  currentProfileId={auth.activeProfileId}
                  presenceMap={presenceMap}
                  t={t}
                />
              ))}
            </ul>
          )}
        </div>
      </aside>

      {/* ============ Right pane — active thread / empty state ============ */}
      <section className={`${activeId || isNewChatRoute ? 'flex' : 'hidden md:flex'} min-w-0 flex-1 flex-col`}>
        {activeId || isNewChatRoute ? (
          <Outlet />
        ) : (
          <ChatRightEmpty t={t} />
        )}
      </section>

      {/* Global message search (chat.SearchMessages, unscoped) — overlays
          the right pane as a slide-in drawer rather than a page nav, so
          the active conversation (if any) isn't lost underneath it.
          Positioned against this surface, not the window: chat can be one pane
          of a split, and a fixed drawer would cover its neighbour. */}
      {showGlobalSearch && (
        <div className="absolute inset-y-0 right-0 z-30 shadow-xl">
          <MessageSearchPanel
            locale={locale}
            onClose={() => setShowGlobalSearch(false)}
            onJump={(conversationId, msgId) => {
              setShowGlobalSearch(false);
              navigate(`/chats/${conversationId}?m=${msgId}`);
            }}
          />
        </div>
      )}
    </div>
  );
});

// ----------------------------------------------------------------------------

function ConversationListItem({
  conversation,
  active,
  locale,
  unread,
  members,
  currentProfileId,
  presenceMap,
  t,
}: {
  conversation: Conversation;
  active: boolean;
  locale: string;
  unread?: UnreadCount;
  members?: ConversationMember[];
  currentProfileId: string | null;
  presenceMap?: Map<string, Presence>;
  t: (key: any, params?: any) => string;
}) {
  const id = conversation.entity?.id;
  const display = resolveConversationDisplay(conversation, currentProfileId, members);
  const isDirect = conversation.type === 'CONVERSATION_TYPE_DIRECT';
  const peerId = isDirect
    ? members?.find(m => m.profile?.entity?.id && m.profile.entity.id !== currentProfileId)?.profile?.entity?.id
    : undefined;
  const peerPresence = peerId ? presenceMap?.get(peerId) : undefined;
  const displayName = display.name.startsWith('chat.') ? t(display.name) : display.name;
  const preview = getLastMessagePreview(conversation);
  const previewText = preview.text
    ? preview.senderName
      ? `${preview.senderName}: ${preview.text}`
      : preview.text
    : preview.textKey
      ? t(preview.textKey)
      : '';
  const unreadCount = unread?.unread_count ?? 0;
  const unreadMentions = unread?.unread_mentions ?? 0;
  const isChannel = conversation.type === 'CONVERSATION_TYPE_CHANNEL';

  return (
    <li>
      <NavLink
        to={`/chats/${id}`}
        className={() =>
          `flex items-center gap-3 border-b border-border px-3 py-2.5 transition-colors duration-150 motion-reduce:transition-none ${
            active ? 'bg-accent' : 'hover:bg-accent/60'
          }`
        }
      >
        {/* Avatar */}
        <div className="relative flex-shrink-0">
          <EntityAvatar name={isChannel ? '#' : displayName} image={display.avatarUrl} profileId={display.peerProfileId} size="md" className="h-10 w-10" />
          {peerPresence?.status && peerPresence.status !== 'PRESENCE_STATUS_OFFLINE' && (
            <span
              className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-background ${presenceDotClass(peerPresence.status)}`}
            />
          )}
        </div>

        {/* Text */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <span className={`truncate text-sm ${unreadCount > 0 ? 'font-semibold text-foreground' : 'font-medium text-foreground'}`}>
              {displayName}
            </span>
            {conversation.last_activity_at && (
              <span className="flex-shrink-0 text-[11px] tabular-nums text-muted-foreground">
                {formatRelativeTime(conversation.last_activity_at, locale)}
              </span>
            )}
          </div>
          <div className="mt-0.5 flex items-center justify-between gap-2">
            <span
              className={`truncate text-xs ${
                unreadCount > 0 ? 'text-foreground' : 'text-muted-foreground'
              }`}
            >
              {previewText}
            </span>
            {unreadCount > 0 && (
              <span
                className={`flex h-4 min-w-[16px] flex-shrink-0 items-center justify-center rounded-full px-1 text-[10px] font-semibold tabular-nums ${
                  unreadMentions > 0
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </div>
        </div>
      </NavLink>
    </li>
  );
}

function ChatRightEmpty({
  t,
}: {
  t: (key: any, params?: any) => string;
}) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 p-10 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
        <svg className="h-8 w-8 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 0 1-2.555-.337A5.972 5.972 0 0 1 5.41 20.97a5.969 5.969 0 0 1-.474-.065 4.48 4.48 0 0 0 .978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25Z" />
        </svg>
      </div>
      <h2 className="text-lg font-semibold text-foreground">{t('chat.emptyTitle')}</h2>
      <p className="max-w-sm text-sm text-muted-foreground">{t('chat.emptyDescription')}</p>
      <Link
        to="/chats/new"
        className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
      >
        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
        </svg>
        {t('chat.newConversation')}
      </Link>
    </div>
  );
}

export default ChatLayout;
